<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { string } from 'yup'
import { emailResetPasswordAction } from '@/api/functions/auth/email-reset-password.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { emailErr, requiredErr } from '@/constants'
import { getErrorMessage } from '@/utils/errorUtils'

const router = useRouter()

const loading = ref(false)
const step = ref(1)
const toast = useToast()

const validationSchema = {
  email: string().required(requiredErr).email(emailErr),
}

const initialValues = {
  email: '',
}

const { mutate: resetPasswordMutate } = useMutationApi({
  mutationKey: ['resetPassword'],
  mutationFn: (payload: { email: string }) => emailResetPasswordAction(payload.email),
  onSuccess: (res) => {
    toast.success('ایمیل با موفقیت ارسال شد.')
    console.log(res.meta.message)
    step.value = 2
    loading.value = false
  },
  onError: (err: unknown) => {
    toast.error(getErrorMessage(err), 'خطا')
    loading.value = false
  },
})

const resetPassword = async (values: { email: string }) => {
  loading.value = true
  await resetPasswordMutate({ email: values.email })
}

const goToLogin = () => {
  router.push('/auth/login')
}

const handleSuccess = () => {
  router.push('/auth/login')
}
</script>

<template>
  <div class="flex flex-col items-center justify-center mb-12" :class="step === 1 ? '-mt8' : ''">
    <div class="w-[352px]">
      <!-- Back Button -->
      <div v-if="step === 1" class="back w-full flex justify-start items-center mb-2">
        <button
          @click="step === 1 ? goToLogin() : (step = 1)"
          class="flex items-center h-10 px-5 rounded-[10px] cursor-pointer font-medium text-[#2d5396] hover:bg-[#DAE4F3] text-[13px] transition-colors"
        >
          <img src="/svg/back-icon.svg" class="me-0.5" width="24" />
          بازگشت
        </button>
      </div>
      <div class="bg-white rounded-2xl p-8">
        <!-- Step 1: Enter Email -->
        <div v-if="step === 1">
          <!-- Title -->
          <div class="text-lg font-bold text-[#1b325a] text-start pb-4">بازیابی رمز عبور</div>

          <!-- Description -->
          <div class="text-base text-[#1b325a] text-right mb-[22px]">
            برای دریافت لینک بازیابی رمز عبور، ایمیل خود را وارد کنید.
          </div>

          <!-- Reset Form -->
          <Form
            :validation-schema="validationSchema"
            :initial-values="initialValues"
            @submit="resetPassword"
            class="space-y-4"
          >
            <!-- Email Field -->
            <Input name="email" label="ایمیل خود را وارد کنید" type="text" />

            <!-- Submit Button -->
            <Button
              :loading="loading"
              type="submit"
              :disabled="loading"
              class="mt-6 w-full rounded-lg text-[#0f1114] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>ارسال لینک بازیابی</span>
            </Button>
          </Form>
        </div>

        <!-- Step 2: Success Message -->
        <div v-else class="text-center">
          <!-- Title -->
          <div class="text-lg font-bold text-[#1b325a] text-start pb-4">بازیابی رمز عبور</div>

          <!-- Success Message -->
          <div class="text-base text-[#1b325a] text-right mb-6">
            ایمیلی حاوی لینک تغییر رمز عبور به آدرس ایمیل شما ارسال خواهد شد. لطفا طبق راهنمای ذکر
            شده در ایمیل اقدام به تغییر رمز عبور خود کنید.
          </div>

          <!-- Success Button -->
          <Button @click="handleSuccess" class="w-full rounded-lg text-[#0f1114] font-medium">
            <span>متوجه شدم</span>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
